import React, { createContext, useContext } from 'react';
import Image from '@tiptap/extension-image';
import { NodeViewWrapper, ReactNodeViewRenderer } from '@tiptap/react';
import { useTranslation } from 'react-i18next';
import { BrushIcon } from '../components/Brand/Icon';
import { TextSelection } from 'prosemirror-state';
import { MEDIA_SYNC } from '../pages/reaction/ReactionEditor/RecordedMediaExtension';
import { validDrawingId } from './model';

export const DrawingContext = createContext(null);

function ImageView({ node }) {
  const drawing = useContext(DrawingContext);
  const { t } = useTranslation();
  return <NodeViewWrapper className="editor-image-node" contentEditable={false}>
    <img src={node.attrs.src} alt={node.attrs.alt || ''} title={node.attrs.title || undefined} draggable={false} />
    {validDrawingId(node.attrs.drawingId) && drawing && <button type="button" className="editor-image-edit"
      aria-label={t('drawing:edit', { defaultValue: 'Edit drawing' })} disabled={drawing.disabled}
      onPointerDown={event => event.preventDefault()}
      onClick={() => drawing.onEdit(node.attrs.drawingId)}><BrushIcon /></button>}
  </NodeViewWrapper>;
}

export default Image.extend({
  addAttributes() {
    return { ...this.parent?.(), drawingId: { default: null,
      parseHTML: element => validDrawingId(element.getAttribute('data-wiw-drawing')) ? element.getAttribute('data-wiw-drawing') : null,
      renderHTML: attrs => validDrawingId(attrs.drawingId) ? { 'data-wiw-drawing': attrs.drawingId } : {},
    } };
  },
  addNodeView() {
    return ReactNodeViewRenderer(ImageView, {
      stopEvent: ({ event }) => Boolean(event.target?.closest?.('.editor-image-edit')),
    });
  },
});

// A single ProseMirror transaction preserves surrounding text and creates real,
// removable blank paragraphs. Do not guess a selection with `imagePos + 2`.
export function insertDrawing(editor, src, drawingId, selection) {
  const { schema } = editor;
  let tr = editor.state.tr;
  if (selection) tr.setSelection(selection.resolve(tr.doc));
  // Inserting a picture must never silently remove a selected attachment or
  // quoted fragment (their deletion has its own confirmation).
  if (!tr.selection.empty) tr.setSelection(TextSelection.near(tr.doc.resolve(tr.selection.to)));
  const at = tr.selection.$from;
  const paragraph = schema.nodes.paragraph;
  const inText = at.parent.isTextblock;
  const from = inText ? at.before() : at.pos;
  const to = inText ? at.after() : at.pos;
  const before = inText && at.parent.content.cut(0, at.parentOffset);
  const after = inText && at.parent.content.cut(at.parentOffset);
  const blank = node => node?.type === paragraph && node.content.size === 0;
  const lines = validDrawingId(drawingId) ? 2 : 1;
  const adjacentBlanks = (pos, direction) => {
    let count = 0;
    let cursor = pos;
    while (count < lines) {
      const resolved = tr.doc.resolve(cursor);
      const node = direction < 0 ? resolved.nodeBefore : resolved.nodeAfter;
      if (!blank(node)) break;
      count += 1; cursor += direction * node.nodeSize;
    }
    return count;
  };
  const nodes = [];
  if (before?.size) nodes.push(at.parent.copy(before));
  for (let i = before?.size ? 0 : adjacentBlanks(from, -1); i < lines; i += 1) nodes.push(paragraph.create());
  nodes.push(schema.nodes.image.create({ src, drawingId }));
  const caret = from + nodes.reduce((size, node) => size + node.nodeSize, 0) + 1;
  for (let i = after?.size ? 0 : adjacentBlanks(to, 1); i < lines; i += 1) nodes.push(paragraph.create());
  if (after?.size) nodes.push(at.parent.copy(after));
  tr.replaceWith(from, to, nodes).setMeta(MEDIA_SYNC, true);
  tr.setSelection(TextSelection.create(tr.doc, caret));
  editor.view.dispatch(tr.scrollIntoView());
}

export function replaceDrawing(editor, id, src) {
  const tr = editor.state.tr.setMeta(MEDIA_SYNC, true);
  let found = false;
  let afterImage;
  tr.doc.descendants((node, pos) => {
    if (node.type.name === 'image' && node.attrs.drawingId === id) {
      tr.setNodeMarkup(pos, undefined, { ...node.attrs, src }); found = true;
      if (afterImage === undefined) afterImage = pos + node.nodeSize;
    }
  });
  if (found) {
    tr.setSelection(TextSelection.near(tr.doc.resolve(afterImage), 1));
    editor.view.dispatch(tr);
  }
  return found;
}
